<template>
  <div>
    <div class="absolute-elements">
      <NavBar :sections="sections" @scroll-to-section="scrollToSection" />
    </div>
    <div class="h-screen w-screen overflow-y-auto bg-base-100" @scroll="handleScroll">
      <WaveAnimation :visible="showWave" />
      
      <!-- Job Detail Hero Section -->
      <section class="section relative overflow-hidden" id="slide1">
        <div class="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900"></div>
        <div class="absolute inset-0 bg-pattern opacity-10"></div>
        
        <!-- Decorative Elements -->
        <div class="absolute top-20 left-10 w-32 h-32 bg-primary/20 rounded-full blur-xl animate-float"></div>
        <div class="absolute bottom-20 right-20 w-48 h-48 bg-secondary/10 rounded-full blur-2xl animate-float-slow"></div>
        
        <div class="relative z-10 flex items-center justify-center min-h-screen px-6">
          <div class="text-center max-w-4xl mx-auto">
            <!-- Job Header -->
            <div class="job-header mb-8">
              <div class="job-meta-info mb-4">
                <span class="job-number">{{ jobData.jobNumber }}</span>
                <span class="job-date">{{ jobData.revDate }}</span>
              </div>
              <h1 class="job-title text-5xl md:text-6xl font-bold text-white mb-4">
                {{ jobData.title }}
              </h1>
              <div class="job-details-grid">
                <div class="detail-item">
                  <font-awesome-icon :icon="['fas', 'map-marker-alt']" class="detail-icon" />
                  <span class="detail-label">Location:</span>
                  <span class="detail-value">{{ jobData.location }}</span>
                </div>
                <div class="detail-item">
                  <font-awesome-icon :icon="['fas', 'user-tie']" class="detail-icon" />
                  <span class="detail-label">Reports to:</span>
                  <span class="detail-value">{{ jobData.reportsTo }}</span>
                </div>
              </div>
            </div>
            
            <!-- Quick Actions -->
            <div class="quick-actions flex flex-col sm:flex-row gap-6 justify-center items-center">
              <button 
                @click="scrollToSection('slide2')"
                class="action-primary inline-flex items-center gap-3 px-8 py-4 bg-primary hover:bg-primary/90 
                       text-white font-semibold rounded-full transition-all duration-300 
                       hover:shadow-lg hover:shadow-primary/30 hover:scale-105"
              >
                <span>Apply Now</span>
                <font-awesome-icon :icon="['fas', 'arrow-right']" class="animate-pulse" />
              </button>
              
              <button 
                @click="scrollToSection('slide2')"
                class="action-secondary inline-flex items-center gap-3 px-8 py-4 
                       border-2 border-white/30 hover:border-white/60 text-white 
                       font-semibold rounded-full transition-all duration-300 
                       hover:bg-white/10 hover:scale-105"
              >
                <span>View Full Description</span>
                <font-awesome-icon :icon="['fas', 'chevron-down']" class="animate-bounce" />
              </button>
              
              <button 
                @click="goBackToListings"
                class="action-tertiary inline-flex items-center gap-3 px-6 py-3 
                       text-white/80 hover:text-white font-medium transition-all duration-300"
              >
                <font-awesome-icon :icon="['fas', 'arrow-left']" />
                <span>Back to Jobs</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      <!-- Job Description Section -->
      <section class="section min-h-screen" id="slide2">
        <div class="section-content">
          <div class="job-description-container">
            
            <!-- Job Description Header -->
            <div class="description-header">
              <div class="header-table">
                <div class="table-row">
                  <div class="table-cell label">REPORTS DIRECTLY:</div>
                  <div class="table-cell value">{{ jobData.reportsTo }}</div>
                  <div class="table-cell label">POSITION LOCATION:</div>
                  <div class="table-cell value">{{ jobData.location }}</div>
                </div>
                <div class="table-row">
                  <div class="table-cell label">JD NUMBER:</div>
                  <div class="table-cell value">{{ jobData.jobNumber }}</div>
                  <div class="table-cell label">DATE:</div>
                  <div class="table-cell value">{{ jobData.revDate }}</div>
                </div>
              </div>
              <div class="company-logo">
                <img src="/img/STE Logo.png" alt="STE Tank & Equipment" />
              </div>
            </div>

            <!-- Scope of Role -->
            <div class="job-section">
              <h3 class="section-title">Scope of role</h3>
              <p class="section-content-text">{{ jobData.scopeOfRole }}</p>
            </div>

            <!-- Key Responsibilities -->
            <div class="job-section">
              <h3 class="section-title">Key Responsibilities</h3>
              <ul class="responsibility-list">
                <li v-for="responsibility in jobData.keyResponsibilities" :key="responsibility">
                  {{ responsibility }}
                </li>
              </ul>
            </div>

            <!-- Professional Experience/Qualifications -->
            <div class="job-section">
              <h3 class="section-title">PROFESSIONAL EXPERIENCE/QUALIFICATIONS</h3>
              
              <div class="qualifications-subsection">
                <h4 class="subsection-title">Minimum Requirements</h4>
                <ul class="qualification-list">
                  <li v-for="requirement in jobData.minimumRequirements" :key="requirement">
                    {{ requirement }}
                  </li>
                </ul>
              </div>

            </div>

            <!-- Apply Now CTA -->
            <div class="apply-cta-section">
              <div class="cta-card">
                <h3>Ready to Join Our Team?</h3>
                <p>Submit your application today and become part of STE's manufacturing excellence team.</p>
                <button @click="scrollToApplication" class="apply-cta-btn">
                  <font-awesome-icon :icon="['fas', 'file-upload']" class="btn-icon" />
                  Apply for This Position
                </button>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>

    <!-- Scroll Navigation -->
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollUp"
        :icon="isUpHovered ? 'angles-up' : 'chevron-up'"
        class="scroll-arrow up-arrow fixed bottom-[60px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToPreviousSection"
        @mouseenter="isUpHovered = true"
        @mouseleave="isUpHovered = false"
      />
    </transition>
    <transition name="fade">
      <font-awesome-icon
        v-if="showScrollDown"
        :icon="isDownHovered ? 'angles-down' : 'chevron-down'"
        class="scroll-arrow down-arrow fixed bottom-[10px] left-[51.5%] transform -translate-x-1/2 text-4xl text-secondary cursor-pointer z-50 hover:scale-125 transition-transform"
        @click="scrollToNextSection"
        @mouseenter="isDownHovered = true"
        @mouseleave="isDownHovered = false"
      />
    </transition>
  </div>
</template>
<script>
import NavBar from '../components/NavBar.vue';
import WaveAnimation from '../components/WaveAnimation.vue';

export default {
  name: 'JobDetail',
  components: {
    NavBar,
    WaveAnimation
  },
  data() {
    return {
      sections: [
        { name: 'JOB OVERVIEW', id: 'slide1' },
        { name: 'FULL DESCRIPTION', id: 'slide2' }
      ],
      showScrollUp: false,
      showScrollDown: true,
      isUpHovered: false,
      isDownHovered: false,
      showWave: false,
      jobsData: {
        'JD00079': {
          jobNumber: 'JD00079',
          revision: 'A',
          revDate: '01/14/2025',
          title: 'MANUFACTURING TRAINING SPECIALIST',
          reportsTo: 'HR Director',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a dynamic and experienced Training Specialist to design, develop, and deliver effective training programs that enhance organizational performance. This position will develop and deliver training on production processes, safety, and equipment for new and existing employees, focusing on hands-on skill-building, standard operating procedures (SOPs), and compliance, while assessing needs, creating materials, tracking progress, and collaborating with management to boost efficiency and maintain a skilled workforce. Key duties include designing curriculum, facilitating sessions (classroom/on-the-floor), evaluating performance, maintaining records, and ensuring training ties to business goals like quality, cost, and safety.',
          keyResponsibilities: [
            'Collaborate with leadership to identify skill gaps, then design, develop, and update training programs, manuals, and materials (e.g., videos, e-learning) for new hires and ongoing training',
            'Conduct engaging, hands-on training on machinery, safety protocols (like PIT operation), quality standards, and SOPs in both classroom and production floor settings',
            'Provide one-on-one coaching, feedback, and support to employees to enhance performance and ensure proper procedure adherence',
            'Assess employee competency through practical tests, monitor performance post-training, track progress, and report on effectiveness to management',
            'Manage training schedules, attendance records, budget, facility setup, and maintain accurate training documentation',
            'Stay current with new manufacturing techniques and technologies, and partner with operations, quality, and safety teams for ongoing improvement',
            'Develop and deliver training in areas including: Onboarding/New hire, Manufacturing, production, soft skills, safety, and equipment',
            'Create Standard work instructions'
          ],
          minimumRequirements: [
            'Proven experience in instructional design, curriculum development, and adult education principles',
            'Excellent project management skills with the ability to handle multiple initiatives simultaneously',
            'Demonstrated teaching or classroom experience combined with outstanding presentation skills',
            'Exceptional written communication skills for content development and documentation purposes',
            'Effective verbal communication skills for delivering engaging training sessions and facilitating discussions',
            'Ability to work independently as well as collaboratively within cross-functional teams',
            'Bachelor\'s degree in Education, Instructional Design, Human Resources, or related field; advanced certifications are a plus',
            'Bi-Lingual (Spanish) required'
          ]
        },
        'JD00080': {
          jobNumber: 'JD00080',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'DESIGN ENGINEER',
          reportsTo: 'Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a detail-oriented Design Engineer to join our engineering team and help design the custom stainless steel tank trailers STE is known for. This role takes customer specifications from concept through release to production, creating 3D models, fabrication drawings, and bills of material for 3-A Sanitary, food grade, DOT407 chemical, ASME, and double conical trailers. The Design Engineer works closely with Sales, Production, Quality, and Purchasing to ensure every design is manufacturable, cost-effective, and compliant with applicable DOT, ASME, 3-A Sanitary, and FMVSS requirements. The ideal candidate enjoys solving practical engineering problems and seeing their designs come to life on the shop floor.',
          keyResponsibilities: [
            'Develop 3D models, detailed fabrication and assembly drawings, and bills of material for custom tank trailers and components using SolidWorks or similar CAD software',
            'Translate customer specifications and sales orders into complete, buildable designs that meet build-to-spec requirements',
            'Ensure designs comply with applicable regulations and standards, including DOT cargo tank requirements (49 CFR Parts 178 & 180), ASME Section VIII, 3-A Sanitary Standards, and FMVSS',
            'Perform engineering calculations such as weight distribution, axle loading, pressure and structural analysis; use FEA where appropriate',
            'Apply GD&T and sheet metal design best practices for stainless steel fabrication, forming, and welding',
            'Support production by answering engineering questions, resolving build issues on the floor, and processing engineering change requests',
            'Collaborate with Quality and Weld Engineering on weld joint design, inspection requirements, and continuous improvement',
            'Develop and maintain standard designs, design libraries, and options to reduce lead times and improve consistency',
            'Work with Purchasing and suppliers to specify components such as valves, pumps, suspensions, and insulation systems',
            'Maintain accurate engineering documentation, revision control, and ERP item and BOM data'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Mechanical Engineering or a related engineering discipline',
            '3+ years of design engineering experience in a manufacturing environment; trailer, tank, pressure vessel, or heavy equipment experience preferred',
            'Proficiency in 3D CAD software (SolidWorks preferred) and producing production-ready drawings',
            'Working knowledge of GD&T, sheet metal design, and welded fabrication',
            'Familiarity with DOT, ASME, or 3-A Sanitary standards is a strong plus',
            'Strong problem-solving skills and the ability to manage multiple projects and deadlines',
            'Excellent written and verbal communication skills and a collaborative, hands-on approach',
            'Experience with ERP/MRP systems and engineering change processes is a plus'
          ]
        },
        'JD00081': {
          jobNumber: 'JD00081',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'QUALITY ENGINEER',
          reportsTo: 'Senior Quality Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a proactive Quality Engineer to support product quality and compliance throughout the fabrication, welding, and final assembly of our stainless steel tank trailers. This role develops and maintains inspection and test plans, leads root cause and corrective action efforts, and partners with Engineering and Production to prevent defects before they happen. The Quality Engineer will help uphold the requirements of STE\'s DOT, ASME, and 3-A Sanitary programs and play a key role in driving continuous improvement across the plant.',
          keyResponsibilities: [
            'Develop, implement, and maintain inspection plans, test procedures, and quality documentation for incoming material, in-process fabrication, and final inspection',
            'Lead root cause analysis and corrective/preventive actions (CAPA) using tools such as 8D, 5-Why, and fishbone diagrams',
            'Manage nonconformance reports (NCRs), material review, and disposition of nonconforming product',
            'Coordinate and witness weld inspection and nondestructive testing, including visual, dye penetrant, and radiographic examination',
            'Oversee hydrostatic and pneumatic pressure testing and related test records for DOT and ASME vessels',
            'Support compliance with DOT cargo tank requirements (49 CFR Parts 178 & 180), ASME Section VIII, and 3-A Sanitary Standards, including audit preparation',
            'Monitor quality metrics such as first pass yield, defect rates, and warranty claims, and drive improvement initiatives',
            'Support supplier quality through receiving inspection, supplier corrective actions, and material certification review',
            'Maintain the gauge and equipment calibration program',
            'Partner with Engineering and Production on design reviews, process improvements, and work instructions'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Engineering (Mechanical, Industrial, Manufacturing, or related) or equivalent experience',
            '3+ years of quality engineering experience in a fabrication, welding, or heavy manufacturing environment',
            'Working knowledge of quality tools and methodologies including root cause analysis, CAPA, SPC, and FMEA',
            'Ability to read and interpret engineering drawings, weld symbols, and GD&T',
            'Familiarity with DOT, ASME, or 3-A Sanitary standards strongly preferred',
            'AWS Certified Welding Inspector (CWI) or ASQ Certified Quality Engineer (CQE) is a plus',
            'Strong communication skills and the ability to work effectively on the production floor and with cross-functional teams',
            'Proficiency with Microsoft Office; ERP/QMS software experience is a plus'
          ]
        },
        'JD00082': {
          jobNumber: 'JD00082',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'SENIOR QUALITY MANAGER',
          reportsTo: 'General Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking an experienced Senior Quality Manager to lead STE\'s quality organization and champion our commitment to precision in every bulk tank and trailer we build. This leadership role owns the quality management system and STE\'s certification programs, including ASME, DOT, and 3-A Sanitary, and serves as the primary point of contact for customer, regulatory, and third-party audits. The Senior Quality Manager will build and develop a high-performing quality team, set quality strategy and metrics, and partner with Engineering, Production, and leadership to drive a culture of quality and continuous improvement as STE grows.',
          keyResponsibilities: [
            'Lead, mentor, and develop the quality team, including quality engineers and inspectors',
            'Own and continuously improve the quality management system, quality manual, and related procedures',
            'Maintain STE\'s ASME certification and quality control program, including coordination with the Authorized Inspection Agency and certification reviews',
            'Oversee compliance with DOT cargo tank requirements (49 CFR Parts 178 & 180) and 3-A Sanitary Standards, including third-party verifications',
            'Serve as the primary contact for customer, regulatory, and third-party audits, and drive timely closure of findings',
            'Establish and report on quality KPIs such as first pass yield, cost of quality, NCRs, and warranty performance',
            'Lead the corrective and preventive action program and ensure effective root cause analysis across the organization',
            'Oversee warranty claims analysis and partner with Service and warranty centers to reduce field issues',
            'Manage supplier quality programs, including supplier qualification, performance monitoring, and corrective actions',
            'Partner with Engineering and Production leadership on new product introduction, process capability, and continuous improvement',
            'Manage the quality department budget, staffing, and training plans'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Engineering or a related technical field; advanced degree is a plus',
            '8+ years of quality experience in heavy manufacturing, fabrication, or pressure vessel production, including 5+ years in a quality leadership role',
            'Demonstrated experience maintaining ASME and/or DOT certification programs and leading external audits',
            'Strong knowledge of quality management systems (ISO 9001 or similar), root cause analysis, and statistical methods',
            'Familiarity with welding inspection and nondestructive examination methods; CWI is a plus',
            'ASQ certification (CQM/OE, CQE) or Six Sigma certification is preferred',
            'Proven ability to lead teams, influence across departments, and drive a culture of quality',
            'Excellent communication, organizational, and decision-making skills'
          ]
        },
        'JD00083': {
          jobNumber: 'JD00083',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'WELD ENGINEER',
          reportsTo: 'Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a knowledgeable Weld Engineer to own the welding processes behind STE\'s stainless steel tank trailers. Constructed by America\'s best engineers and welders, our trailers depend on consistent, code-quality welds, from structural joints to sanitary-finish product contact surfaces. This role develops and qualifies welding procedures, supports manual and robotic welding operations, and works with Quality, Design Engineering, and Production to improve weld quality, reduce distortion and rework, and increase productivity.',
          keyResponsibilities: [
            'Develop, qualify, and maintain Welding Procedure Specifications (WPS) and Procedure Qualification Records (PQR) in accordance with ASME Section IX and applicable AWS codes',
            'Administer welder performance qualifications (WPQ) and continuity records',
            'Provide technical support for GTAW, GMAW, plasma, and other welding processes used on stainless steel sheet and plate',
            'Partner with the Robotic Weld Programmer to develop and optimize robotic and automated seam welding processes',
            'Troubleshoot weld defects, distortion, and porosity, and lead root cause and corrective actions with Quality and Production',
            'Establish best practices for back purging, heat input control, cleaning, and passivation to meet sanitary and corrosion-resistance requirements',
            'Review weld joint designs and weld symbols with Design Engineering for manufacturability and code compliance',
            'Evaluate and implement new welding equipment, consumables, fixtures, and technologies',
            'Train and coach welders on procedures, techniques, and quality expectations',
            'Support audits and inspections related to ASME, DOT, and 3-A Sanitary programs'
          ],
          minimumRequirements: [
            'Bachelor\'s degree in Welding Engineering, Materials Science, Mechanical Engineering, or a related field',
            '3+ years of welding engineering experience, preferably with stainless steel fabrication',
            'Strong working knowledge of ASME Section IX; familiarity with AWS D1.1/D1.6 and ASME Section VIII is preferred',
            'Hands-on knowledge of GTAW and GMAW processes; experience with robotic or automated welding is a plus',
            'Ability to read and interpret engineering drawings and weld symbols',
            'AWS Certified Welding Inspector (CWI) or Certified Welding Engineer (CWEng) preferred',
            'Strong analytical, problem-solving, and communication skills',
            'Comfortable working on the production floor alongside welders and supervisors'
          ]
        },
        'JD00084': {
          jobNumber: 'JD00084',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'TIG WELDER',
          reportsTo: 'Production Supervisor',
          location: 'STE; on site',
          scopeOfRole: 'We are hiring multiple skilled TIG Welders to join our production team in building premium stainless steel tank trailers. In this role you will perform high-quality GTAW (TIG) welding on tanks, barrels, heads, and components, working from blueprints and welding procedures to produce code-quality welds and sanitary finishes. This is a hands-on role for a craftsperson who takes pride in their work and wants to build equipment that safely transports the nation\'s milk, food products, and chemicals.',
          keyResponsibilities: [
            'Perform GTAW (TIG) welding on stainless steel sheet, plate, and tubing in multiple positions, following approved Welding Procedure Specifications (WPS)',
            'Read and interpret blueprints, fabrication drawings, and weld symbols',
            'Fit up, tack, and weld components, tanks, and assemblies to specified tolerances',
            'Perform back purging and proper cleaning techniques to produce sound, corrosion-resistant welds',
            'Grind, blend, and finish welds to meet sanitary and cosmetic requirements',
            'Inspect own work and ensure welds meet visual, dye penetrant, and radiographic inspection standards',
            'Work safely inside tanks and confined spaces following all safety procedures and PPE requirements',
            'Maintain welding equipment and keep the work area clean and organized',
            'Collaborate with team members, supervisors, and Quality to meet production schedules'
          ],
          minimumRequirements: [
            '2+ years of TIG welding experience, preferably on stainless steel',
            'Ability to read blueprints and weld symbols',
            'Ability to pass a company weld test and qualify to company procedures (ASME Section IX)',
            'Experience with fit-up, back purging, and weld finishing on stainless steel is preferred',
            'Ability to lift up to 50 lbs, stand for extended periods, and work in confined spaces',
            'Strong attention to detail and commitment to quality and safety',
            'High school diploma or GED; welding certification or technical school training is a plus',
            'Bi-Lingual (Spanish) is a plus'
          ]
        },
        'JD00085': {
          jobNumber: 'JD00085',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'ROBOTIC WELD PROGRAMMER',
          reportsTo: 'Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a Robotic Weld Programmer to program, optimize, and support the robotic and automated welding cells used to fabricate our stainless steel tanks and components. This role is responsible for creating and refining weld programs, improving cycle times and weld consistency, and keeping automated equipment running reliably. Working closely with the Weld Engineer, Production, and Maintenance, the Robotic Weld Programmer will play an important part in growing STE\'s automation capabilities.',
          keyResponsibilities: [
            'Create, edit, and optimize robotic welding programs using teach pendant and offline programming software',
            'Set up and prove out new parts, fixtures, and weld programs to meet quality and cycle time targets',
            'Tune weld parameters, seam tracking, and touch sensing for consistent, high-quality welds on stainless steel',
            'Troubleshoot robotic cells, welding power sources, and related equipment to minimize downtime',
            'Partner with the Weld Engineer to ensure robotic processes conform to qualified welding procedures',
            'Work with Design Engineering on fixture design and design-for-automation improvements',
            'Train operators on safe operation, setup, and basic troubleshooting of robotic welding cells',
            'Perform and coordinate preventive maintenance on robotic welding equipment',
            'Maintain program backups, documentation, and change records',
            'Follow and enforce robotic cell safety procedures'
          ],
          minimumRequirements: [
            '3+ years of experience programming robotic welding systems (FANUC, Lincoln, Yaskawa/Motoman, or ABB)',
            'Strong understanding of welding fundamentals, especially GMAW and GTAW processes',
            'Experience with offline programming software (e.g., ROBOGUIDE, MotoSim, RobotStudio) is a plus',
            'Ability to read and interpret blueprints and weld symbols',
            'Mechanical and electrical troubleshooting skills',
            'Associate\'s degree in Robotics, Automation, Welding Technology, or a related field, or equivalent experience',
            'Strong problem-solving skills and the ability to work independently',
            'Commitment to safety and quality'
          ]
        },
        'JD00086': {
          jobNumber: 'JD00086',
          revision: 'A',
          revDate: '10/22/2026',
          title: 'TECHNICAL SPECIFICATIONS WRITER',
          reportsTo: 'Engineering Manager',
          location: 'STE; on site',
          scopeOfRole: 'We are seeking a detail-driven Technical Specifications Writer to translate customer orders into clear, accurate build specifications for every STE trailer. Because each trailer is built to spec, this role is essential to making sure what the customer ordered is exactly what Engineering designs and Production builds. The Technical Specifications Writer will work closely with Sales, Engineering, Purchasing, and Production to document tank size and configuration, insulation, valves, pumps, suspension, lighting, and other options, and to maintain the standard specifications and documentation that support our product lines.',
          keyResponsibilities: [
            'Translate customer orders, quotes, and sales notes into detailed, accurate build specifications for each trailer',
            'Prepare and maintain customer-facing specification sheets and technical documentation',
            'Develop and maintain a library of standard specifications and option descriptions for all product lines',
            'Review specifications with Sales and Engineering to confirm feasibility, completeness, and regulatory compliance',
            'Communicate specification details to Engineering, Purchasing, and Production and answer questions throughout the build',
            'Manage specification revisions and change orders, ensuring all departments are working from current documents',
            'Support document control processes, including revision history and archiving',
            'Write or update technical documents such as operator manuals and work instructions as needed',
            'Identify opportunities to standardize and simplify specifications to reduce errors and lead times'
          ],
          minimumRequirements: [
            '3+ years of experience in technical writing, specification writing, or a similar role in a manufacturing environment',
            'Ability to read and interpret engineering drawings and technical documents',
            'Knowledge of trailers, trucks, tanks, or heavy equipment is a strong plus',
            'Exceptional written communication skills and attention to detail',
            'Strong organizational skills and the ability to manage multiple orders and deadlines',
            'Proficiency in Microsoft Office; experience with ERP systems is a plus',
            'Ability to work collaboratively with Sales, Engineering, and Production teams',
            'Associate\'s or Bachelor\'s degree in a technical field, Technical Communication, or related field, or equivalent experience'
          ]
        }
      }
    };
  },
  computed: {
    jobData() {
      const jobId = this.$route.params.id;
      return this.jobsData[jobId] || this.getDefaultJobData();
    }
  },
  mounted() {
    // Initialize scroll state
    const container = document.querySelector('.overflow-y-auto');
    if (container) {
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    }
  },
  methods: {
    getDefaultJobData() {
      return {
        jobNumber: 'Not Found',
        revision: '',
        revDate: '',
        title: 'Position Not Found',
        reportsTo: '',
        location: '',
        scopeOfRole: 'The requested job position could not be found.',
        keyResponsibilities: [],
        minimumRequirements: []
      };
    },

    handleScroll(event) {
      const container = event.target;
      this.showScrollUp = container.scrollTop > 100;
      this.showScrollDown = container.scrollTop + container.clientHeight < container.scrollHeight - 100;
      this.showWave = container.scrollTop > container.clientHeight / 2;
    },
    
    scrollToSection(id) {
      console.log(`Scrolling to section: ${id}`);
      const container = document.querySelector('.overflow-y-auto');
      const element = document.getElementById(id);
      if (element) {
        container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
      }
    },
    
    scrollToNextSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      const nextSection = sections.find(
        (section) => section.offsetTop > currentScroll + tolerance
      );

      if (nextSection) {
        console.log(`Scrolling to next section: ${nextSection.id}`);
        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
      } else {
        console.log('No next section found');
      }
    },
    
    scrollToPreviousSection() {
      const container = document.querySelector('.overflow-y-auto');
      const sections = Array.from(document.querySelectorAll('section'));
      const currentScroll = container.scrollTop;
      const tolerance = 5;

      let previousSection = null;
      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section.offsetTop + tolerance < currentScroll) {
          previousSection = section;
          break;
        }
      }

      if (previousSection) {
        console.log(`Scrolling to previous section: ${previousSection.id}`);
        container.scrollTo({ top: previousSection.offsetTop, behavior: 'smooth' });
      } else {
        this.scrollToTop();
      }
    },
    
    scrollToTop() {
      const container = document.querySelector('.overflow-y-auto');
      container.scrollTo({ top: 0, behavior: 'smooth' });
    },

    goBackToListings() {
      this.$router.push('/careers');
    },

    scrollToApplication() {
      // Navigate back to careers page and scroll to application
      this.$router.push('/careers').then(() => {
        this.$nextTick(() => {
          setTimeout(() => {
            const container = document.querySelector('.overflow-y-auto');
            const element = document.getElementById('slide2');
            if (element && container) {
              container.scrollTo({ top: element.offsetTop, behavior: 'smooth' });
            }
          }, 100);
        });
      });
    }
  }
};
</script>

<style scoped>
.absolute-elements {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  z-index: 100;
}

.h-screen.overflow-y-auto {
  background: radial-gradient(ellipse at bottom, var(--color-base-100) 0%, var(--color-base-300) 90%);
  background-attachment: fixed;
  position: relative;
  scrollbar-width: none;
  z-index: 0;
  -webkit-overflow-scrolling: touch;
}

.section {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;
  overflow: hidden;
}

.section-content {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

/* Hero Section Styles */
.bg-pattern {
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
}

/* Job Header */
.job-header {
  text-align: center;
}

.job-meta-info {
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 1rem;
}

.job-number, .job-date {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  font-weight: 500;
}

.job-title {
  background: linear-gradient(135deg, #ffffff 0%, #b7b9ba 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  text-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
  line-height: 1.1;
  margin-bottom: 2rem;
}

.job-details-grid {
  display: flex;
  justify-content: center;
  gap: 3rem;
  margin-bottom: 3rem;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: rgba(255, 255, 255, 0.9);
  font-size: 1.1rem;
  font-weight: 500;
}

.detail-icon {
  color: #a22a21;
  font-size: 1.2rem;
}

.detail-label {
  color: #a22a21;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.detail-value {
  color: rgba(255, 255, 255, 0.9);
}

/* Quick Actions */
.quick-actions {
  margin-bottom: 2rem;
}

.action-primary, .action-secondary, .action-tertiary {
  min-width: 200px;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-primary:hover, .action-secondary:hover, .action-tertiary:hover {
  text-decoration: none;
  color: white;
}

/* Job Description Container */
.job-description-container {
  background: rgba(255, 255, 255, 0.98);
  border-radius: 20px;
  padding: 3rem;
  margin: 2rem 0;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(162, 42, 33, 0.1);
}

/* Description Header Table */
.description-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 3rem;
  padding-bottom: 2rem;
  border-bottom: 3px solid #a22a21;
}

.header-table {
  flex: 1;
}

.table-row {
  display: grid;
  grid-template-columns: auto 1fr auto 1fr;
  gap: 1rem;
  margin-bottom: 0.5rem;
  align-items: center;
}

.table-row:first-child {
  grid-template-columns: auto 1fr auto 1fr;
}

.table-cell.label {
  font-weight: bold;
  color: #2d3748;
  font-size: 0.9rem;
  white-space: nowrap;
}

.table-cell.value {
  color: #4a5568;
  font-size: 0.9rem;
}

.company-logo {
  flex-shrink: 0;
  margin-left: 2rem;
}

.company-logo img {
  height: 60px;
  width: auto;
}

/* Job Sections */
.job-section {
  margin-bottom: 3rem;
}

.section-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 1.5rem;
  border-bottom: 2px solid #a22a21;
  padding-bottom: 0.5rem;
}

.section-content-text {
  color: #4a5568;
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 1rem;
}

/* Lists */
.responsibility-list, .qualification-list {
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
}

.responsibility-list li, .qualification-list li {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 0.75rem;
  color: #4a5568;
  line-height: 1.6;
  text-align: left;
}

.responsibility-list li::before, .qualification-list li::before {
  content: '•';
  position: absolute;
  left: 0.5rem;
  color: #a22a21;
  font-weight: bold;
  font-size: 1.2rem;
}

/* Qualifications Subsections */
.qualifications-subsection {
  margin-bottom: 2.5rem;
}

.subsection-title {
  font-size: 1.2rem;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 1rem;
  text-decoration: underline;
  text-decoration-color: #a22a21;
}


/* Apply CTA Section */
.apply-cta-section {
  margin-top: 4rem;
  text-align: center;
}

.cta-card {
  background: linear-gradient(135deg, rgba(162, 42, 33, 0.1), rgba(183, 185, 186, 0.05));
  border: 2px solid rgba(162, 42, 33, 0.2);
  border-radius: 20px;
  padding: 3rem;
  backdrop-filter: blur(10px);
}

.cta-card h3 {
  font-size: 2rem;
  color: #2d3748;
  margin-bottom: 1rem;
  font-weight: 700;
}

.cta-card p {
  color: #4a5568;
  margin-bottom: 2rem;
  font-size: 1.1rem;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.apply-cta-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: linear-gradient(135deg, #a22a21, #8f0000);
  color: white;
  padding: 1.25rem 2.5rem;
  border: none;
  border-radius: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  font-size: 1.1rem;
}

.apply-cta-btn:hover {
  background: linear-gradient(135deg, #8f0000, #7d0000);
  transform: translateY(-3px);
  box-shadow: 0 12px 32px rgba(162, 42, 33, 0.4);
}

/* Animations */
@keyframes float {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(2deg); 
  }
}

@keyframes float-slow {
  0%, 100% { 
    transform: translateY(0) rotate(0deg); 
  }
  50% { 
    transform: translateY(-30px) rotate(-3deg); 
  }
}

.animate-float {
  animation: float 8s ease-in-out infinite;
}

.animate-float-slow {
  animation: float-slow 12s ease-in-out infinite;
}

/* Fade Transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
}

/* Responsive Design */
@media (max-width: 768px) {
  .job-title {
    font-size: 2.5rem;
  }
  
  .job-details-grid {
    flex-direction: column;
    gap: 1rem;
  }
  
  .quick-actions {
    flex-direction: column;
    gap: 1rem;
  }
  
  .action-primary, .action-secondary {
    width: 100%;
    max-width: 300px;
  }
  
  .job-description-container {
    padding: 2rem;
    margin: 1rem 0;
  }
  
  .description-header {
    flex-direction: column;
    gap: 2rem;
  }
  
  .company-logo {
    margin-left: 0;
    text-align: center;
  }
  
  .table-row {
    grid-template-columns: auto 1fr;
    gap: 0.5rem;
  }
  
  .table-row:nth-child(2) {
    grid-template-columns: auto 1fr auto 1fr;
  }
  
  .cta-card {
    padding: 2rem;
  }
  
  .cta-card h3 {
    font-size: 1.5rem;
  }
  
  .job-meta-info {
    flex-direction: column;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .job-title {
    font-size: 2rem;
  }
  
  .job-description-container {
    padding: 1.5rem;
  }
  
  .section-title {
    font-size: 1.25rem;
  }
  
  .table-row {
    grid-template-columns: 1fr;
    gap: 0.25rem;
  }
  
  .table-cell.label {
    font-weight: bold;
    margin-bottom: 0.25rem;
  }
  
  .table-cell.value {
    margin-bottom: 0.75rem;
    padding-left: 1rem;
  }
  
  .cta-card {
    padding: 1.5rem;
  }
}
</style>
